import { Injectable } from '@angular/core';
import { DashboardType } from 'src/app/dashboards/models/enums/dashboard-type';
import {
  IOrganizationHierarchyResponse,
  IOrganizationResponse,
  OrganizationHierarchyFlowType,
} from 'src/app/dashboards/models/spatial.models';
import { BaseType, TwinTypes } from '../../models/space-types.enum';
import { filterHierarchyByDescendantBaseType } from '../hierarchy/helpers/hierarchy-filters';
import { IMenuItem } from './menu-item.service';

@Injectable({
  providedIn: 'root',
})
export class MenuHierarchyService {
  /**
   * Map organization response to menu items
   */
  public mapOrganizationToMenuItems(
    organization: IOrganizationResponse,
    flowType?: OrganizationHierarchyFlowType,
  ): IMenuItem[] {
    const menuItem = {
      id: organization.id,
      name: organization.name,
      type: TwinTypes.Organization.toLowerCase(),
      twinType: TwinTypes.Organization,
      baseType: BaseType.Agent,
      queryParams: {},
      selected: false,
      selectable: false,
      collapsed: false,
      collapseDisabled: true,
    } as IMenuItem;
    menuItem.children = this.mapHierarchyToMenuItems(organization.hierarchy, undefined, menuItem, flowType);
    return [menuItem];
  }

  /**
   * Map hierarchy response to menu items recursively
   */
  public mapHierarchyToMenuItems(
    hierarchy: IOrganizationHierarchyResponse[],
    rootNodes?: IOrganizationHierarchyResponse[],
    parent?: IMenuItem,
    flowType?: OrganizationHierarchyFlowType,
  ): IMenuItem[] {
    const menuItems = [];
    rootNodes = rootNodes ?? hierarchy.filter((x) => !x.parentId);

    for (const node of rootNodes) {
      const menuItem = {
        id: node.id,
        name: node.name,
        type: node.type,
        twinType: node.twinType,
        children: [],
        baseType: node.baseType,
        queryParams: { flow: flowType?.toLowerCase(), objecten: node.id },
        parent,
        selected: false,
        selectable: true,
        collapsed: true,
        collapseDisabled: false,
        subscriptionAccessSource: node.subscriptionAccessSource,
      } as IMenuItem;

      menuItem.children = this.mapHierarchyToMenuItems(
        hierarchy,
        hierarchy.filter((x) => x.parentId == node.id),
        menuItem,
        flowType,
      );
      menuItems.push(menuItem);
    }
    return menuItems.length ? menuItems : [];
  }

  /**
   * Map a mutation payload to a menu item
   */
  public mapPayloadToMenuItem(
    id: string,
    name: string,
    twinType: TwinTypes,
    baseType: BaseType,
    parent?: IMenuItem,
    flowType?: OrganizationHierarchyFlowType,
  ): IMenuItem {
    return {
      id,
      name,
      type: twinType.toLowerCase(),
      twinType,
      baseType,
      children: [],
      queryParams: { flow: flowType?.toLowerCase(), objecten: id },
      parent,
      selected: false,
      selectable: true,
      collapsed: true,
      collapseDisabled: false,
    } as IMenuItem;
  }

  /**
   * Filter menu items based on dashboard type
   */
  public filterMenuItemsByDashboardType(menuItems: IMenuItem[], dashboardType: DashboardType): IMenuItem[] {
    let baseTypeFilter: BaseType | undefined;

    switch (dashboardType) {
      case DashboardType.Energy:
      case DashboardType.Climate:
        // Only show menu items that contain assets
        baseTypeFilter = BaseType.Asset;
        break;
      case DashboardType.History:
        baseTypeFilter = BaseType.Point;
        break;
      default:
        // No menu item filtering for other dashboard types
        return menuItems;
    }

    const organizationNode = menuItems[0]; // There always is 1 top-level node (organization)
    const filteredHierarchy = filterHierarchyByDescendantBaseType(organizationNode, [
      baseTypeFilter,
    ]) as IMenuItem | null;

    if (!filteredHierarchy) {
      organizationNode.children = [];
      return [organizationNode];
    }

    return [filteredHierarchy];
  }
}
